import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  signal,
} from '@angular/core';
import { NavigationEnd, Router, RouterOutlet } from '@angular/router';
import { toSignal } from '@angular/core/rxjs-interop';
import { filter, map } from 'rxjs';
import {
  AvatarComponent,
  BadgeComponent,
  BreadcrumbsComponent,
  IconComponent,
  MenuComponent,
  SidebarComponent,
} from '../../../shared/common-components';
import type { BreadcrumbItem, MenuItem, SidebarItem } from '../../../shared/common-components';
import { AuthService } from '../../../features/auth/services/auth.service';
import { environment } from '../../../../environments/environment';

const ROUTE_LABELS: Record<string, string> = {
  dashboard: 'Tableau de bord',
  'my-tasks': 'Mes tâches',
  projects: 'Projets',
  tasks: 'Tâches',
  users: 'Utilisateurs',
  calendar: 'Calendrier',
  settings: 'Paramètres',
  profile: 'Profil',
};

@Component({
  selector: 'app-layout',
  imports: [
    RouterOutlet,
    SidebarComponent,
    BreadcrumbsComponent,
    IconComponent,
    AvatarComponent,
    BadgeComponent,
    MenuComponent,
  ],
  templateUrl: './app-layout.html',
  styleUrl: './app-layout.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AppLayoutComponent {
  private readonly router = inject(Router);
  private readonly auth = inject(AuthService);

  /** Navigation latérale principale. */
  readonly navItems: SidebarItem[] = [
    { label: 'Tableau de bord', link: '/dashboard', icon: 'dashboard' },
    { label: 'Mes tâches', link: '/my-tasks', icon: 'inbox' },
    { label: 'Projets', link: '/projects', icon: 'folder' },
    { label: 'Tâches', link: '/tasks', icon: 'list' },
    { label: 'Utilisateurs', link: '/users', icon: 'users' },
    { label: 'Calendrier', link: '/calendar', icon: 'calendar' },
    { label: 'Paramètres', link: '/settings', icon: 'settings' },
  ];

  /** Menu utilisateur (en haut à droite). */
  readonly userMenuItems: MenuItem[] = [
    { label: 'Profil', value: 'profile' },
    { label: 'Paramètres', value: 'settings' },
    { label: 'Se déconnecter', value: 'logout' },
  ];

  /** Mode démo actif (badge affiché dans l'en-tête). */
  protected readonly mockMode = environment.mock;

  protected readonly collapsed = signal(false);
  protected readonly mobileOpen = signal(false);

  private readonly url = toSignal(
    this.router.events.pipe(
      filter((event): event is NavigationEnd => event instanceof NavigationEnd),
      map((event) => event.urlAfterRedirects),
    ),
    { initialValue: this.router.url },
  );

  /** Fil d'Ariane dérivé de l'URL courante. */
  protected readonly breadcrumbs = computed<BreadcrumbItem[]>(() => {
    const items: BreadcrumbItem[] = [{ label: 'Accueil', link: '/dashboard', icon: 'home' }];
    const parts = (this.url() ?? '').split('/').filter((segment) => segment.length > 0);

    parts.forEach((segment, index) => {
      const label =
        ROUTE_LABELS[segment] ??
        (index === parts.length - 1 ? decodeURIComponent(segment) : segment);
      items.push({
        label,
        link: index < parts.length - 1 ? `/${parts.slice(0, index + 1).join('/')}` : undefined,
      });
    });
    return items;
  });

  protected toggleCollapsed(): void {
    this.collapsed.update((value) => !value);
  }

  protected toggleMobile(): void {
    this.mobileOpen.update((value) => !value);
  }

  protected closeMobile(): void {
    this.mobileOpen.set(false);
  }

  onUserMenu(item: MenuItem): void {
    switch (item.value) {
      case 'profile':
        this.router.navigate(['/profile']);
        break;
      case 'settings':
        this.router.navigate(['/settings']);
        break;
      case 'logout':
        this.auth.logout();
        this.router.navigate(['/login']);
        break;
    }
  }
}
